import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, SITE } from "@/data/site";
import CloudinaryImage from "@/components/CloudinaryImage";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const bookHref = `https://wa.me/${SITE.phoneE164}?text=${encodeURIComponent(
    SITE.whatsappMessage
  )}`;

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A1612]/85 border-b border-white/10 shadow-[0_8px_30px_-15px_rgba(0,0,0,0.5)]"
          : "backdrop-blur-md bg-[#0A1612]/40 border-b border-white/5"
      }`}
      style={{ backdropFilter: "blur(10px)" }}
      data-testid="site-navbar"
    >
      <div className="container-pam flex items-center justify-between py-3 md:py-4">
        <a
          href="#top"
          className="flex items-center gap-3 group"
          data-testid="nav-brand-link"
        >
          <span className="flex items-center justify-center bg-[#f8f5ef] rounded-full shadow-xl ring-2 ring-[var(--pam-gold)]/60 overflow-hidden w-14 h-14 md:w-16 md:h-16" style={{ padding: 5 }}>
            <CloudinaryImage
              publicId={SITE.cloudinary.logoNav}
              fallback={SITE.logo}
              alt="Peak Adventures Malnad"
              className="w-full h-full object-contain"
              eager
              data-testid="nav-logo"
            />
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm text-white/85 hover:text-[var(--pam-gold)] transition-colors duration-200 tracking-wide"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:+${SITE.phoneE164}`}
            className="btn-ghost-light rounded-full px-4 py-2 text-sm flex items-center gap-2"
            data-testid="nav-call-btn"
          >
            <Phone className="w-4 h-4" />
            <span>Call to Book</span>
          </a>
          <a
            href={bookHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold rounded-full px-5 py-2 text-sm font-medium"
            data-testid="nav-book-btn"
          >
            Book Now
          </a>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[#0A1612]/95 backdrop-blur-xl overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="container-pam py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-white/85 hover:text-[var(--pam-gold)] text-base tracking-wide py-1"
                  data-testid={`mnav-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
              <div className="flex gap-3 pt-3 border-t border-white/10">
                <a
                  href={`tel:+${SITE.phoneE164}`}
                  className="btn-ghost-light rounded-full px-4 py-2 text-sm flex items-center gap-2"
                >
                  <Phone className="w-4 h-4" /> Call to Book
                </a>
                <a
                  href={bookHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold rounded-full px-5 py-2 text-sm font-medium"
                >
                  Book Now
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
